import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  ToggleButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { DedicatedDeliveryAgent } from "../../../../../app/api/dedicatedDelivery/DedicatedDeliveryAgent";
import {
  DdStoreClaims,
  DdStorePerformanceResponse,
} from "../../../../../app/models/responseModels/DdStorePerformanceResponse";
import { CustomTooltip } from "../../../../../components/common/tooltip/CustomToolTip";
import { getDefaultMRTOptions } from "../../../DefaultMrtOptions";
import { RootState } from "../../../../../app/features/store/store";
import { FdpResponse } from "../../../../../app/models/responseModels/FdpResponse";
import { format, parseISO } from "date-fns";

interface StoreClaimsProps {
  metaData: DdStorePerformanceResponse;
}

const StoreClaims: React.FC<StoreClaimsProps> = ({ metaData }) => {
  const defaultMRTOptions_claims = getDefaultMRTOptions<DdStoreClaims>();
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});

  const [invoiceClaims, setInvoiceClaims] = useState<any>(
    [...metaData.ddStoreClaims].sort((a, b) => 
      new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
    )
  );

  const { authResponse } = useSelector((state: RootState) => state.login);

  const claimColumns = useMemo<MRT_ColumnDef<DdStoreClaims>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        enableEditing: false,
      },
      {
        accessorKey: "claimAmount",
        header: "Claim Amount",
        muiEditTextFieldProps: {
          type: "number",
          required: true,
          error: !!validationErrors?.claimAmount,
          helperText: validationErrors?.claimAmount,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              claimAmount: undefined,
            }),
        },
      },
      {
        accessorKey: "fdpId",
        header: "FDP",
        Cell: ({ row }) => row.original.fdpName,
        editVariant: "select",
        muiEditTextFieldProps: ({ row }) => ({
          select: true,
          required: true,
          children: metaData.fdps.map((fdp: FdpResponse) => (
            <MenuItem key={fdp.id} value={fdp.id}>
              {fdp.companyName}
            </MenuItem>
          )),
          error: !!validationErrors?.fdpId,
          helperText: validationErrors?.fdpId,
          onFocus: () => {
            setValidationErrors((prevErrors) => ({
              ...prevErrors,
              fdpId: undefined,
            }));
          },
        }),
      },
      {
        accessorKey: "operationDate",
        header: "Date",
        Cell: ({ cell, row }) =>
          row.original.operationDate &&
          format(parseISO(row.original.operationDate), "MM/dd/yyyy"),
        muiEditTextFieldProps: {
          type: "date",
          required: true,
          error: !!validationErrors?.operationDate,
          helperText: validationErrors?.operationDate,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              operationDate: undefined,
            }),
        },
      },
      {
        accessorKey: "isEnabled",
        header: "Active",
        Cell: ({ cell, row }) => (row.original.isEnabled ? "Yes" : "No"),
        editSelectOptions: [
          { label: "Yes", text: "Yes", value: true },
          { label: "No", text: "No", value: false },
        ],
        editVariant: "select",
        muiEditTextFieldProps: {
          select: true,
          error: !!validationErrors?.isEnabled,
          helperText: validationErrors?.isEnabled,
          onFocus: () => {
            // Reset error on focus
            setValidationErrors((prevErrors) => ({
              ...prevErrors,
              isEnabled: undefined,
            }));
          },
        },
      },
      {
        accessorKey: "isInvoiced",
        header: "Is Invoiced",
        Cell: ({ cell, row }) => (row.original.isInvoiced ? "TRUE" : "FALSE"),
        enableEditing: false,
      },
      {
        accessorKey: "createdBy",
        header: "Created By",
        enableEditing: false,
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        enableEditing: false,
      },
      {
        accessorKey: "createdOn",
        header: "Created On",
        enableEditing: false,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.createdOn ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h12",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        accessorKey: "updatedOn",
        header: "Updated On",
        enableEditing: false,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.updatedOn ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h12",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
    ],
    [validationErrors]
  );

  const validateClaim = (values: any) => {
    const errors = {
      claimAmount: !values.claimAmount 
        ? "Claim Amount is Required" 
        : parseFloat(values.claimAmount) < 0 
          ? "Claim Amount cannot be negative" 
          : "",
      fdpId: !values.fdpId ? "FDP is Required" : "",
      date: !values.operationDate ? "Date is Required" : "",
    };
    
    return errors;
  };

  const handleSaveClaim: MRT_TableOptions<DdStoreClaims>["onEditingRowSave"] =
    async ({ values, table, row }) => {
      console.log(values);
      const newValidationErrors = validateClaim(values);
      if (Object.values(newValidationErrors).some((error) => error)) {
        setValidationErrors(newValidationErrors);
        return;
      }
      setValidationErrors({});
      try {
        updateClaimsAsync(values, row.original.id);
      } catch (e) {
        toast.error(
          "Server error occurred while submiting claim data. Please try again later."
        );
      } finally {
        table.setEditingRow(null);
      }
    };

  const updateClaimsAsync = async (values: any, id: any) => {
    const response = await DedicatedDeliveryAgent.updateClaimToStore({
      id: id,
      ddStoreId: metaData.id,
      fdpId: values.fdpId,
      operationDate: values.operationDate,
      claimAmount: values.claimAmount,
      isEnabled: values.isEnabled,
      isInvoiced: values.isInvoiced,
      updatedBy: authResponse?.userSettings?.userName ?? "",
    });
    if (response && response.id) {
      toast.success("Claim is modified successfully");
      setInvoiceClaims((prev: any) => {
        const updatedClaims = prev.map((claim: any) =>
          claim.id === response.id ? response : claim
        );
        // Sort claims after updating
        return updatedClaims.sort((a: any, b: any) => 
          new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
        );
      });
    }
  };

  const handleToggleClaim = async (row: MRT_Row<DdStoreClaims>) => {
    await updateClaimsAsync(
      {
        id: row.original.id,
        ddStoreId: row.original.ddStoreId,
        fdpId: row.original.fdpId,
        operationDate: row.original.operationDate,
        claimAmount: row.original.claimAmount,
        isEnabled: !row.original.isEnabled,
        isInvoiced: row.original.isInvoiced,
        updatedBy: authResponse?.userSettings?.userName ?? "",
      },
      row.original.id
    );
  };

  const handleCreateClaim: MRT_TableOptions<DdStoreClaims>["onCreatingRowSave"] =
    async ({ values, table }) => {
      console.log(values, metaData.id);
      const newValidationErrors = validateClaim(values);
      if (Object.values(newValidationErrors).some((error) => error)) {
        setValidationErrors(newValidationErrors);
        return;
      }
      setValidationErrors({});
      try {
        const response = await DedicatedDeliveryAgent.addClaimToStore({
          ddStoreId: metaData.id,
          claimAmount: values.claimAmount,
          operationDate: values.operationDate,
          fdpId: values.fdpId,
          isEnabled: values.isEnabled,
          isInvoiced: values.isInvoiced,
          createdBy: authResponse?.userSettings?.userName ?? "",
          updatedBy: authResponse?.userSettings?.userName ?? "",
        });
        if (response && response.id) {
          toast.success("A Claim has been added.");
          setInvoiceClaims((prev: any) => {
            const updatedClaims = [...prev, response];
            // Sort claims after adding a new one
            return updatedClaims.sort((a, b) => 
              new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
            );
          });
        }
      } catch (e) {
        toast.error(
          "Server error occurred while submiting claim data. Please try again later."
        );
      } finally {
        table.setCreatingRow(null);
      }
    };

  // preprare MRT V2 grid
  const claimsTable = useMaterialReactTable({
    ...defaultMRTOptions_claims,
    columns: claimColumns,
    data: invoiceClaims,
    getRowId: (row) => row.id.toString(),
    createDisplayMode: "row",
    editDisplayMode: "row",
    enableEditing: true,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleSaveClaim,
    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateClaim,
    initialState: {
      ...defaultMRTOptions_claims.initialState,
      columnVisibility: {
        id: false,
      },
      sorting: [{ desc: true, id: "createdOn" }],
    },
    state: {
      sorting: [{ desc: true, id: "createdOn" }],
    },
    renderBottomToolbarCustomActions: () => {
      return (
        <Typography variant="subtitle2" component="p">
          Total Rows: {invoiceClaims.length}
        </Typography>
      );
    },
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: "flex", gap: "1rem" }}>
        <Tooltip title="Edit">
          <IconButton
            onClick={() => {
              setValidationErrors({});
              table.setEditingRow(row);
            }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
        <ToggleButton
          value="Enable/Disable"
          selected={row.original.isEnabled}
          onChange={() => handleToggleClaim(row)}
        >
          <BlockIcon />
        </ToggleButton>
      </Box>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <CustomTooltip
        arrow
        title="Add New"
        onClick={() => {
          setValidationErrors({});
          table.setCreatingRow(true);
        }}
      >
        <IconButton type="button">
          <AddIcon sx={{ color: "primary.main" }} />
        </IconButton>
      </CustomTooltip>
    ),
    muiTableBodyRowProps: ({ row }) => ({
      ...defaultMRTOptions_claims.muiTableBodyRowProps,
      sx: {
        bgcolor: row.original.isEnabled === false ? "grey" : undefined,
      },
    }),
  });

  return <MaterialReactTable table={claimsTable} />;
};

export default StoreClaims;
